import { useEffect, useState } from 'react';
import { ArrowRight, Check, ChevronLeft, RotateCcw } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { formatCurrency } from '../blocos/tipos';
import { seloDoRisco, textoDoRisco } from './riscoFila';
import { CardBento, CabecalhoLista, VerMais } from './ui';
import { useMascara } from '../../valores-ocultos/contexto';
import {
  ACAO_FILA,
  destinoDaLinha,
  pluralFila,
  type ItemAgrupado,
} from './useFilaDoDia';

/**
 * Âncora deste card na página. Exportada porque o "Próximos passos" do Feed
 * abre o Dashboard com `?foco=` nela, e o Dashboard rola até aqui — uma string
 * repetida nos dois lados quebraria em silêncio no dia em que alguém mudasse
 * um dos dois.
 */
export const ANCORA_FILA = 'fila-de-hoje';

/**
 * A fila de hoje: quem contatar, do mais urgente pro menos, uma pessoa por
 * linha e uma ação por linha.
 *
 * ── A FILA DO GERENTE FALA COM VENDEDOR, NÃO COM CLIENTE ────────────────────
 * (Pedido de 2026-08-25.) A view (seller_action_queue, determinística — o
 * agente NÃO escreve nela) entrega ao gerente os itens do time crus, e o card
 * mostrava "cobrar o cliente X" como se o cliente fosse dele. Agora, em modo
 * gestão: tarefa de venda PRÓPRIA do gerente aparece direta, como pra
 * qualquer vendedor; tarefa de subordinado vira UMA linha "Fale com
 * {vendedor}" somando as pendências dele — o detalhe por cliente fica no
 * drill-down do modal, que é o contexto pra conversa com o vendedor.
 *
 * ── CHECKLIST ───────────────────────────────────────────────────────────────
 * O ✓ marca a linha como vista e ela SOME — lista sobe, trabalha-se de cima
 * pra baixo. Marca por DIA e por dispositivo (localStorage com a data na
 * chave; a fila é recomputada todo dia, então ontem expira sozinho).
 * "Restaurar" desfaz o dia. Na linha de vendedor, o ✓ marca o GRUPO.
 *
 * ABRIR TAMBÉM MARCA. Clicar no botão de ação leva para o item E dá o visto:
 * quem clicou foi resolver. Antes era preciso voltar e clicar no ✓, e ninguém
 * voltava — a fila repetia o mesmo item o dia inteiro.
 *
 * "Ver as N pendências" abre a fila completa num modal (expandir no lugar
 * quebrava a grade do bento): TODAS as pendências, uma por linha, da mais
 * nova para a mais atrasada, e N é o tamanho dessa lista (pedido do Pedro,
 * 28/10/2026 — na gestão o modal mostrava só as linhas "Fale com", ~10). O card some quando a fila do banco está vazia;
 * zerada PELA PESSOA, ele fica e diz isso.
 */

/** Quantas linhas o card mostra recolhido — 4 pra página caber numa
 *  tela de PC sem scroll (pedido de 2026-08-25); o resto mora no modal. */
const MAX = 4;

const chaveDoDia = () => `north.filaFeitas.${new Date().toISOString().slice(0, 10)}`;

const lerFeitas = (): Set<string> => {
  try {
    const raw = window.localStorage.getItem(chaveDoDia());
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
};

const gravarFeitas = (feitas: Set<string>) => {
  try {
    window.localStorage.setItem(chaveDoDia(), JSON.stringify([...feitas]));
  } catch {
    // localStorage bloqueado: o checklist só não persiste, nada quebra.
  }
};

const chaveItem = (i: ItemAgrupado) => `${i.kind}|${(i.contact_name ?? '').trim().toUpperCase()}`;

const diasAte = (iso: string | null): number | null => {
  if (!iso) return null;
  const hoje = new Date();
  const alvo = new Date(`${iso}T00:00:00`);
  return Math.round(
    (alvo.getTime() - new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate()).getTime()) /
      86_400_000,
  );
};

const seloDaLinha = (i: ItemAgrupado): { texto: string; tom: string } => {
  if (i.kind === 'cobranca_inadimplente') return { texto: 'atraso', tom: 'text-[#b91c1c]' };
  if (i.kind === 'lance_assembleia') {
    const dias = diasAte(i.event_date);
    if (dias == null) return { texto: 'lance', tom: 'text-[#b45309]' };
    if (dias <= 0) return { texto: 'hoje', tom: 'text-[#b91c1c]' };
    return {
      texto: `${dias} ${dias === 1 ? 'dia' : 'dias'}`,
      tom: dias <= 2 ? 'text-[#b91c1c]' : 'text-[#b45309]',
    };
  }
  if (i.kind === 'venda_pendente' && (i.days_waiting ?? 0) > 0) {
    return { texto: `${i.days_waiting} dias`, tom: 'text-[#5b6572]' };
  }
  return { texto: 'hoje', tom: 'text-[#5b6572]' };
};

/** "2 cobranças · 1 lance · 1 pagamento" — o resumo da carteira do vendedor. */
const CONTAGEM_LABEL: Record<string, [string, string]> = {
  cobranca_inadimplente: ['cobrança', 'cobranças'],
  lance_assembleia: ['lance', 'lances'],
  venda_pendente: ['pagamento', 'pagamentos'],
  followup_proposta: ['follow-up', 'follow-ups'],
};

type GrupoVendedor = {
  sid: number;
  nome: string;
  itens: ItemAgrupado[];
  prioridade: number;
  valor: number;
};

type LinhaExibida =
  | { tipo: 'item'; item: ItemAgrupado; prioridade: number }
  | { tipo: 'vendedor'; grupo: GrupoVendedor; prioridade: number };

export const CardFilaDeHoje = ({
  itens,
  navigate,
  gestao,
  col = 2,
}: {
  itens: ItemAgrupado[];
  navigate: (to: string) => void;
  /** Presente = modo gestão: itens de subordinados viram "Fale com {vendedor}". */
  gestao?: { propriaId: number | null; nomes: Map<number, string> };
  /** Largura na grade: 4 pro vendedor (linha inteira), 2 na gestão (divide com Equipe). */
  col?: 2 | 4;
}) => {
  const m = useMascara();
  const [feitas, setFeitas] = useState<Set<string>>(() => lerFeitas());
  const [modalAberto, setModalAberto] = useState(false);
  const [vendedorFoco, setVendedorFoco] = useState<GrupoVendedor | null>(null);

  // Outra aba do mesmo navegador pode marcar itens — recarrega ao focar.
  useEffect(() => {
    const aoFocar = () => setFeitas(lerFeitas());
    window.addEventListener('focus', aoFocar);
    return () => window.removeEventListener('focus', aoFocar);
  }, []);

  if (itens.length === 0) return null;

  const marcar = (chave: string) => {
    setFeitas((prev) => {
      const next = new Set(prev);
      next.add(chave);
      gravarFeitas(next);
      return next;
    });
  };

  const restaurar = () => {
    const vazio = new Set<string>();
    gravarFeitas(vazio);
    setFeitas(vazio);
  };

  // ── monta as linhas exibidas ──────────────────────────────────────────────
  const linhas: LinhaExibida[] = [];
  if (!gestao) {
    for (const i of itens) {
      linhas.push({ tipo: 'item', item: i, prioridade: i.priority });
    }
  } else {
    const porVendedor = new Map<number, GrupoVendedor>();
    for (const i of itens) {
      const sid = i.sales_person_id;
      // Venda própria do gerente (ou sem dono resolvido): tarefa direta.
      if (sid == null || sid === gestao.propriaId) {
        linhas.push({ tipo: 'item', item: i, prioridade: i.priority });
        continue;
      }
      const g = porVendedor.get(sid) ?? {
        sid,
        nome: gestao.nomes.get(sid) ?? `Vendedor ${sid}`,
        itens: [],
        prioridade: i.priority,
        valor: 0,
      };
      g.itens.push(i);
      g.prioridade = Math.min(g.prioridade, i.priority);
      g.valor += Number(i.amount ?? 0);
      porVendedor.set(sid, g);
    }
    for (const g of porVendedor.values()) {
      linhas.push({ tipo: 'vendedor', grupo: g, prioridade: g.prioridade });
    }
    linhas.sort((a, b) => a.prioridade - b.prioridade);
  }

  const chaveLinha = (l: LinhaExibida) =>
    l.tipo === 'item' ? chaveItem(l.item) : `vend|${l.grupo.sid}`;

  const visiveis = linhas.filter((l) => !feitas.has(chaveLinha(l)));
  const mostradas = visiveis.slice(0, MAX);
  const concluidasHoje = linhas.length - visiveis.length;

  // A fila inteira, item a item, para o modal. Item de vendedor cujo grupo
  // levou o visto sai junto com o grupo.
  const doSubordinado = (i: ItemAgrupado) =>
    gestao != null && i.sales_person_id != null && i.sales_person_id !== gestao.propriaId;
  const pendencias = itens
    .filter(
      (i) =>
        !feitas.has(chaveItem(i)) &&
        !(doSubordinado(i) && feitas.has(`vend|${i.sales_person_id}`)),
    )
    .sort((a, b) => (a.days_waiting ?? 0) - (b.days_waiting ?? 0));

  const resumoDoGrupo = (g: GrupoVendedor) => {
    const porKind = new Map<string, number>();
    for (const i of g.itens) porKind.set(i.kind, (porKind.get(i.kind) ?? 0) + i.quantas);
    const partes = [...porKind.entries()].map(([kind, n]) => {
      const [sing, plu] = CONTAGEM_LABEL[kind] ?? ['item', 'itens'];
      return `${m(n)} ${n === 1 ? sing : plu}`;
    });
    return `${partes.join(' · ')}${g.valor > 0 ? ` · ${m(formatCurrency(g.valor, true))}` : ''}`;
  };

  const linhaItem = (i: ItemAgrupado, idx: number, comVendedor = false) => {
    const selo = seloDaLinha(i);
    // Risco de exclusão (migration 219): selo ao lado do nome e, no detalhe,
    // quanto falta para a exclusão pelo contrato.
    const risco = seloDoRisco(i);
    const textoRisco = textoDoRisco(i);
    return (
      <div
        key={chaveItem(i)}
        className="flex items-center gap-3 border-b border-[rgba(53,70,96,0.09)] py-2.5 last:border-b-0 sm:gap-4"
      >
        <button
          type="button"
          onClick={() => marcar(chaveItem(i))}
          title="Marcar como visto (remove da fila de hoje)"
          aria-label={`Marcar ${i.contact_name ?? 'item'} como visto`}
          className="flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded-full border border-[rgba(53,70,96,0.35)] text-transparent transition-colors hover:border-[#059669] hover:bg-[#059669] hover:text-white"
        >
          <Check className="h-3 w-3" />
        </button>
        <span className={`w-14 shrink-0 text-xs font-semibold ${selo.tom}`}>{selo.texto}</span>
        <div className="min-w-0 flex-1">
          <p className="flex min-w-0 items-center gap-2 text-[14px] font-semibold tracking-[-0.01em]">
            <span className="truncate">{i.contact_name ?? '—'}</span>
            {risco && (
              <span
                title={risco.explicacao}
                className="shrink-0 rounded-full bg-[#fee2e2] px-2 py-0.5 text-[10px] font-semibold tracking-normal text-[#b91c1c] dark:bg-red-950/60 dark:text-red-300"
              >
                {risco.texto}
              </span>
            )}
          </p>
          <p className="mt-0.5 truncate text-xs text-[#6b7480] dark:text-muted-foreground">
            {comVendedor && doSubordinado(i)
              ? `${gestao!.nomes.get(i.sales_person_id!) ?? 'Vendedor'} · `
              : ''}
            {i.quantas > 1 ? `${m(i.quantas)} ${pluralFila(i.kind)}` : (i.detail ?? '')}
            {textoRisco ? ` · ${textoRisco}` : ''}
            {i.amount != null && Number(i.amount) > 0
              ? ` · ${m(formatCurrency(Number(i.amount), true))}`
              : ''}
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            // Ir trabalhar no item JÁ marca como visto: quem clicou está
            // resolvendo, e obrigar a voltar depois só para clicar no ✓ fazia
            // a fila mentir o dia inteiro. "Restaurar" desfaz o dia se o
            // clique foi engano.
            marcar(chaveItem(i));
            navigate(destinoDaLinha(i));
          }}
          className={`inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-full px-[13px] py-1.5 text-xs font-semibold ${
            idx === 0
              ? 'bg-primary text-primary-foreground hover:bg-primary/90'
              : 'border border-[rgba(53,70,96,0.2)] hover:border-[rgba(53,70,96,0.4)]'
          }`}
        >
          {ACAO_FILA[i.kind] ?? 'Abrir'}
          <ArrowRight className="h-[13px] w-[13px]" />
        </button>
      </div>
    );
  };

  const linhaVendedor = (g: GrupoVendedor) => (
    <div
      key={`vend|${g.sid}`}
      className="flex items-center gap-3 border-b border-[rgba(53,70,96,0.09)] py-2.5 last:border-b-0 sm:gap-4"
    >
      <button
        type="button"
        onClick={() => marcar(`vend|${g.sid}`)}
        title="Marcar como visto (remove da fila de hoje)"
        aria-label={`Marcar pendências de ${g.nome} como vistas`}
        className="flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded-full border border-[rgba(53,70,96,0.35)] text-transparent transition-colors hover:border-[#059669] hover:bg-[#059669] hover:text-white"
      >
        <Check className="h-3 w-3" />
      </button>
      <span className="w-14 shrink-0 text-xs font-semibold text-[#5b6572]">equipe</span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-[14px] font-semibold tracking-[-0.01em]">
          Fale com {g.nome}
        </p>
        <p className="mt-0.5 truncate text-xs text-[#6b7480] dark:text-muted-foreground">
          {resumoDoGrupo(g)}
        </p>
      </div>
      <button
        type="button"
        // Aqui NÃO marca: este botão abre o detalhamento do time, não resolve
        // nada. Dar o visto esconderia o grupo enquanto o gerente ainda está
        // lendo as pendências dentro do modal.
        onClick={() => {
          setVendedorFoco(g);
          setModalAberto(true);
        }}
        className="inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-full border border-[rgba(53,70,96,0.2)] px-[13px] py-1.5 text-xs font-semibold hover:border-[rgba(53,70,96,0.4)]"
      >
        Ver pendências
        <ArrowRight className="h-[13px] w-[13px]" />
      </button>
    </div>
  );

  const desenhar = (l: LinhaExibida, idx: number) =>
    l.tipo === 'item' ? linhaItem(l.item, idx) : linhaVendedor(l.grupo);

  return (
    // A âncora existe para o chip do resumo, no topo da MESMA tela, conseguir
    // rolar até aqui em vez de mandar a pessoa para outra página.
    <CardBento id={ANCORA_FILA} col={col} className="flex flex-col !px-5 !py-4">
      <CabecalhoLista
        titulo="Fila de hoje"
        extra={`${
          concluidasHoje > 0
            ? `${m(concluidasHoje)} ${concluidasHoje === 1 ? 'concluída' : 'concluídas'} hoje · `
            : ''
        }${gestao ? 'toda a equipe · por urgência' : 'ordenada por urgência'}`}
      />
      <div className="mt-1 flex-1">
        {visiveis.length === 0 ? (
          <p className="py-6 text-center text-[13px] text-[#6b7480] dark:text-muted-foreground">
            Nenhuma pendência na fila de hoje.
          </p>
        ) : (
          mostradas.map((l, idx) => desenhar(l, idx))
        )}
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        {pendencias.length > mostradas.length ? (
          <VerMais
            onClick={() => {
              setVendedorFoco(null);
              setModalAberto(true);
            }}
          >
            Ver as {m(pendencias.length)} pendências
          </VerMais>
        ) : (
          <span />
        )}
        {concluidasHoje > 0 && (
          <button
            type="button"
            onClick={restaurar}
            className="mt-3 inline-flex cursor-pointer items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
          >
            <RotateCcw className="h-3 w-3" />
            Restaurar as vistas
          </button>
        )}
      </div>

      {/* A fila completa (ou a de UM vendedor), sem mexer na grade. */}
      <Dialog
        open={modalAberto}
        onOpenChange={(o) => {
          setModalAberto(o);
          if (!o) setVendedorFoco(null);
        }}
      >
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-baseline gap-2">
              {vendedorFoco ? `Pendências de ${vendedorFoco.nome}` : 'Fila de hoje'}
              <span className="text-xs font-normal text-muted-foreground">
                {vendedorFoco
                  ? resumoDoGrupo(vendedorFoco)
                  : `${m(pendencias.length)} ${pendencias.length === 1 ? 'pendência' : 'pendências'}${
                      concluidasHoje > 0 ? ` · ${m(concluidasHoje)} concluídas` : ''
                    }`}
              </span>
            </DialogTitle>
          </DialogHeader>
          {vendedorFoco && (
            <button
              type="button"
              onClick={() => setVendedorFoco(null)}
              className="inline-flex cursor-pointer items-center gap-1 self-start text-xs font-semibold text-primary hover:text-primary/80"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              Toda a fila
            </button>
          )}
          <div className="max-h-[65vh] overflow-y-auto pr-1">
            {vendedorFoco ? (
              vendedorFoco.itens
                .filter((i) => !feitas.has(chaveItem(i)))
                .map((i, idx) => linhaItem(i, idx))
            ) : pendencias.length === 0 ? (
              <p className="py-6 text-center text-[13px] text-[#6b7480] dark:text-muted-foreground">
                Nenhuma pendência na fila de hoje.
              </p>
            ) : (
              pendencias.map((i, idx) => linhaItem(i, idx, true))
            )}
          </div>
          {!vendedorFoco && concluidasHoje > 0 && (
            <button
              type="button"
              onClick={restaurar}
              className="inline-flex cursor-pointer items-center gap-1 self-start text-xs font-medium text-muted-foreground hover:text-foreground"
            >
              <RotateCcw className="h-3 w-3" />
              Restaurar as vistas
            </button>
          )}
        </DialogContent>
      </Dialog>
    </CardBento>
  );
};
